import { type CSSProperties, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { Badge, Button, ConfirmationDialog, FormInput, Modal } from '@components/UI';

import type { ScreenerExperiment } from '../hooks/useScreenerExperiments';
import { diffScreenerGroupInputs, formatPct01Number, formatSignedPct, profitColorClass } from '../utils/screenerExperimentUtils';

function clampStyle(lineCount: number): CSSProperties {
    return {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lineCount,
        overflow: 'hidden'
    };
}

function sortExperiments(experiments: ScreenerExperiment[]): ScreenerExperiment[] {
    return [...experiments].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function metricRows(exp: ScreenerExperiment) {
    const m = exp.metrics.inSample;
    return [
        { label: 'Avg Trade', value: formatSignedPct(m.avgProfit, 2), raw: m.avgProfit },
        { label: 'Raw return sum', value: formatSignedPct(m.totalProfit, 2), raw: m.totalProfit },
        { label: 'Lift', value: formatSignedPct(m.lift, 2), raw: m.lift },
        { label: 'Coverage', value: formatPct01Number(m.coverage, 1), raw: m.coverage },
        { label: 'Win', value: formatPct01Number(m.winRate, 1), raw: m.winRate },
        { label: 'Trades', value: `${m.passingCount}/${m.totalCount}`, raw: m.passingCount }
    ] as const;
}

function CompareModal({ a, b, isOpen, onClose }: { a: ScreenerExperiment; b: ScreenerExperiment; isOpen: boolean; onClose: () => void }) {
    const diff = useMemo(() => diffScreenerGroupInputs(a.config.rootGroupInput, b.config.rootGroupInput), [a, b]);

    const aRows = useMemo(() => metricRows(a), [a]);
    const bRows = useMemo(() => metricRows(b), [b]);

    const rows = useMemo(
        () =>
            aRows.map((rowA, idx) => {
                const rowB = bRows[idx];

                if (rowA.label === 'Trades') {
                    return { label: rowA.label, a: rowA.value, b: rowB.value, diff: '—', diffClassName: 'text-zinc-400' };
                }

                const rawA = Number(rowA.raw);
                const rawB = Number(rowB.raw);

                const isRate = rowA.label === 'Coverage' || rowA.label === 'Win';

                const delta = isRate ? (rawB - rawA) * 100 : rawB - rawA;
                const deltaLabel = formatSignedPct(delta, isRate ? 1 : 2);
                const diffClassName = profitColorClass(delta);

                return { label: rowA.label, a: rowA.value, b: rowB.value, diff: deltaLabel, diffClassName };
            }),
        [aRows, bRows]
    );

    return (
        <Modal closeOnEsc closeOnOverlayClick isOpen={isOpen} size="xl" title="Compare experiments" onClose={onClose}>
            <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="border border-zinc-800 rounded-lg bg-zinc-900/30 p-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-white truncate">{a.label}</div>
                                <div className="text-xs text-zinc-500">
                                    job {a.jobId} • {dayjs(a.createdAt).format('MMM D, YYYY h:mm A')}
                                </div>
                            </div>
                            {a.starred && <Badge variant="secondary">starred</Badge>}
                        </div>
                        <div className="mt-2 text-xs text-zinc-400" style={clampStyle(3)}>
                            {a.ruleSummary || '—'}
                        </div>
                    </div>

                    <div className="border border-zinc-800 rounded-lg bg-zinc-900/30 p-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-white truncate">{b.label}</div>
                                <div className="text-xs text-zinc-500">
                                    job {b.jobId} • {dayjs(b.createdAt).format('MMM D, YYYY h:mm A')}
                                </div>
                            </div>
                            {b.starred && <Badge variant="secondary">starred</Badge>}
                        </div>
                        <div className="mt-2 text-xs text-zinc-400" style={clampStyle(3)}>
                            {b.ruleSummary || '—'}
                        </div>
                    </div>
                </div>

                <div className="border border-zinc-800 rounded-lg overflow-hidden">
                    <div className="bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">Metrics (in-sample)</div>
                    <table className="min-w-full text-xs">
                        <thead className="bg-zinc-900/20 text-zinc-400">
                            <tr>
                                <th className="text-left px-3 py-2">Metric</th>
                                <th className="text-left px-3 py-2">A</th>
                                <th className="text-left px-3 py-2">B</th>
                                <th className="text-left px-3 py-2">Δ (B-A)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr className="border-t border-zinc-800" key={r.label}>
                                    <td className="px-3 py-2 text-zinc-300">{r.label}</td>
                                    <td className="px-3 py-2 text-white">{r.a}</td>
                                    <td className="px-3 py-2 text-white">{r.b}</td>
                                    <td className={`px-3 py-2 ${r.diffClassName}`}>{r.diff}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="border border-zinc-800 rounded-lg overflow-hidden">
                        <div className="bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">Removed (A only)</div>
                        <div className="max-h-[280px] overflow-auto px-3 py-2 text-xs font-mono space-y-1">
                            {diff.removed.length ? (
                                diff.removed.map((l, idx) => (
                                    <div className="text-red-400 whitespace-pre-wrap break-words" key={`${idx}-${l}`}>
                                        - {l}
                                    </div>
                                ))
                            ) : (
                                <div className="text-zinc-500">No removals.</div>
                            )}
                        </div>
                    </div>

                    <div className="border border-zinc-800 rounded-lg overflow-hidden">
                        <div className="bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">Added (B only)</div>
                        <div className="max-h-[280px] overflow-auto px-3 py-2 text-xs font-mono space-y-1">
                            {diff.added.length ? (
                                diff.added.map((l, idx) => (
                                    <div className="text-green-400 whitespace-pre-wrap break-words" key={`${idx}-${l}`}>
                                        + {l}
                                    </div>
                                ))
                            ) : (
                                <div className="text-zinc-500">No additions.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

interface ExperimentsPanelProps {
    experiments: ScreenerExperiment[];
    onLoadExperiment: (exp: ScreenerExperiment) => void;
    onDeleteExperiment: (id: string) => void;
    onUpdateExperiment: (id: string, patch: Partial<ScreenerExperiment>) => void;
    onClearExperiments: () => void;
    className?: string;
}

const ExperimentsPanel = ({
    experiments,
    onLoadExperiment,
    onDeleteExperiment,
    onUpdateExperiment,
    onClearExperiments,
    className
}: ExperimentsPanelProps) => {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    const [compareIds, setCompareIds] = useState<[string, string] | null>(null);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const sorted = sortExperiments(experiments);
        if (!q.length) return sorted;

        return sorted.filter((e) => {
            const haystack = `${e.label} ${e.note ?? ''} ${e.ruleSummary ?? ''} job:${e.jobId}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [experiments, search]);

    const selected = useMemo(() => filtered.filter((e) => selectedIds.has(e.id)), [filtered, selectedIds]);

    const compareA = compareIds ? (experiments.find((e) => e.id === compareIds[0]) ?? null) : null;
    const compareB = compareIds ? (experiments.find((e) => e.id === compareIds[1]) ?? null) : null;

    const canCompare = selected.length === 2;

    return (
        <div className={className}>
            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="text-white font-medium">Experiments</div>
                    <div className="text-xs text-zinc-500">{experiments.length} saved snapshots</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        disabled={!canCompare}
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            if (!canCompare) return;
                            setCompareIds([selected[0].id, selected[1].id]);
                        }}
                    >
                        Compare
                    </Button>
                    <Button disabled={experiments.length === 0} size="sm" variant="danger" onClick={() => setClearConfirmOpen(true)}>
                        Clear
                    </Button>
                </div>
            </div>

            <FormInput label="Search" placeholder="label, job:123, rule text…" value={search} onChange={(e) => setSearch(e.target.value)} />

            <div className="mt-3 border border-zinc-800 rounded-lg overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                    {filtered.map((e) => {
                        const m = e.metrics.inSample;
                        const selected = selectedIds.has(e.id);
                        return (
                            <div
                                className={`border-t border-zinc-900 first:border-t-0 px-3 py-2 hover:bg-zinc-900/40 ${
                                    selected ? 'bg-zinc-900/40' : ''
                                }`}
                                key={e.id}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="pt-1">
                                        <input
                                            aria-label="Select for compare"
                                            checked={selected}
                                            className="accent-p314-blue"
                                            type="checkbox"
                                            onChange={(ev) => {
                                                const { checked } = ev.target;

                                                setSelectedIds((prev) => {
                                                    const next = new Set(prev);
                                                    if (checked) next.add(e.id);
                                                    else next.delete(e.id);
                                                    return next;
                                                });
                                            }}
                                        />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <button className="text-left min-w-0" type="button" onClick={() => onLoadExperiment(e)}>
                                                <div className="text-sm text-white truncate">{e.label}</div>
                                                <div className="text-[11px] text-zinc-500">
                                                    job {e.jobId} • {dayjs(e.createdAt).fromNow()} •{' '}
                                                    {dayjs(e.createdAt).format('MMM D, h:mm A')}
                                                </div>
                                            </button>

                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    aria-label={e.starred ? 'Unstar experiment' : 'Star experiment'}
                                                    className={`rounded px-2 py-1 text-xs border ${
                                                        e.starred
                                                            ? 'border-yellow-600/60 text-yellow-300 bg-yellow-900/10 hover:bg-yellow-900/20'
                                                            : 'border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
                                                    }`}
                                                    type="button"
                                                    onClick={() => onUpdateExperiment(e.id, { starred: !e.starred })}
                                                >
                                                    {e.starred ? '★' : '☆'}
                                                </button>

                                                <Button size="sm" variant="secondary" onClick={() => onLoadExperiment(e)}>
                                                    Load
                                                </Button>

                                                <button
                                                    aria-label="Delete experiment"
                                                    className="rounded px-2 py-1 text-xs border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                                                    type="button"
                                                    onClick={() => setDeleteConfirm({ id: e.id, label: e.label })}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>

                                        {e.note && <div className="mt-1 text-xs text-zinc-400">{e.note}</div>}

                                        <div className="mt-1 text-[11px] text-zinc-400" style={clampStyle(2)}>
                                            {e.ruleSummary || '—'}
                                        </div>

                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <Badge variant="secondary">
                                                avg <span className={profitColorClass(m.avgProfit)}>{formatSignedPct(m.avgProfit, 2)}</span>
                                            </Badge>
                                            <Badge variant="secondary">
                                                return sum{' '}
                                                <span className={profitColorClass(m.totalProfit)}>{formatSignedPct(m.totalProfit, 2)}</span>
                                            </Badge>
                                            <Badge variant="secondary">
                                                lift <span className={profitColorClass(m.lift)}>{formatSignedPct(m.lift, 2)}</span>
                                            </Badge>
                                            <Badge variant="secondary">cov {formatPct01Number(m.coverage, 1)}</Badge>
                                            <Badge variant="secondary">win {formatPct01Number(m.winRate, 1)}</Badge>
                                            <Badge variant="secondary">
                                                trades {m.passingCount}/{m.totalCount}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {filtered.length === 0 && (
                        <div className="px-3 py-6 text-sm text-zinc-400 text-center">
                            {experiments.length === 0 ? 'No experiments yet. Capture from Performance to build a history.' : 'No matches.'}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationDialog
                confirmText="Clear"
                confirmVariant="danger"
                isConfirmLoading={false}
                isOpen={clearConfirmOpen}
                message="Clear all saved experiments for this screener? This only affects your browser."
                title="Clear experiments"
                onCancel={() => setClearConfirmOpen(false)}
                onConfirm={() => {
                    onClearExperiments();
                    setSelectedIds(new Set());
                    setClearConfirmOpen(false);
                }}
            />

            <ConfirmationDialog
                confirmText="Delete"
                confirmVariant="danger"
                isConfirmLoading={false}
                isOpen={deleteConfirm !== null}
                message={deleteConfirm ? `Delete "${deleteConfirm.label}"?` : ''}
                title="Delete experiment"
                onCancel={() => setDeleteConfirm(null)}
                onConfirm={() => {
                    if (!deleteConfirm) return;
                    onDeleteExperiment(deleteConfirm.id);

                    setSelectedIds((prev) => {
                        const next = new Set(prev);
                        next.delete(deleteConfirm.id);
                        return next;
                    });

                    setDeleteConfirm(null);
                }}
            />

            {compareA && compareB && <CompareModal isOpen a={compareA} b={compareB} onClose={() => setCompareIds(null)} />}
        </div>
    );
};

export default ExperimentsPanel;
